// Components
import React from 'react';
import useSWR from 'swr';
import {Center, LoadingSpinner} from '@tempo/core';
import {Heading, Text} from '@tempo/core';
import Head from 'next/head';
import {serverSideTranslations} from '@app/modules/locale-management/server';
import {LicenseCard} from '@app/components/license';
import {TempoLandingPage} from '@app/components/layout/LandingPage';
import {fetcher} from '@app/utils';
import {config} from '@modules/config';

// Types
import type {JSX} from 'react';
import type {GetStaticProps} from 'next';
import type LicensesManifest from '@public/licenses-manifest.json';
import {LandingSection} from '@tempo/landing';

const ERROR_MESSAGE =
	'An arror has occured. Please refresh or try again later.';
const LANDING_PAGE_TITLE = 'Open Source Software Report';
const manifestUrl = `${config.get('basePath')}/licenses-manifest.json`;

const WrapperTempoLandingPage = ({
	children,
}: {
	children: React.ReactNode;
}): JSX.Element => (
	<TempoLandingPage title={LANDING_PAGE_TITLE}>{children}</TempoLandingPage>
);

const LicensesPage: React.FC = () => {
	const {data: manifest, error} = useSWR<typeof LicensesManifest>(
		manifestUrl,
		fetcher,
	);

	if (error) {
		return (
			<WrapperTempoLandingPage>
				<p>{ERROR_MESSAGE}</p>
			</WrapperTempoLandingPage>
		);
	}

	if (!manifest) {
		return (
			<>
				<WrapperTempoLandingPage>
					<Center>
						<LoadingSpinner />
					</Center>
				</WrapperTempoLandingPage>
			</>
		);
	}

	return (
		<>
			<Head>
				<meta name="robots" content="noindex,nofollow" />
			</Head>
			<WrapperTempoLandingPage>
				<LandingSection py={{base: 0, md: 2}}>
					<Heading>{LANDING_PAGE_TITLE}</Heading>
					<Text my="2">
						The following sets forth attribution notices for third party
						softwares that may be contained in portions of the Deezer web
						application.
					</Text>
					{Object.entries(manifest).map(([module, info]) => (
						<LicenseCard key={module} module={module} info={info} />
					))}
				</LandingSection>
			</WrapperTempoLandingPage>
		</>
	);
};

export const getStaticProps: GetStaticProps = async ({locale}) => {
	const translations = await serverSideTranslations(locale ?? 'en', ['common']);
	return {props: {...translations}};
};

export default LicensesPage;
